import { bridge, isNil, localStorageBridge } from '../../utils';
import { useCreateLikeFolderBottomSheet } from './hooks/useCreateLikeFolderBottomSheet';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { likeGoodsService } from '../../api';
import { useCreateNewLikeFolderBottomSheet } from './hooks/useCreateNewLikeFolderBottomSheet';
import { systemAlert } from '../../component/systemAlert';
import { toast } from './toast';
import { queryKey } from '../../constant';
import type { GetMemberInfoResponse } from '../../api/getMemberInfo/types';
import { useShowAnonymousLikeCountLimitAlert } from './hooks/useShowAnonymousLikeCountLimitAlert';
import { useMyInfo } from '../../queries';
import type { LogAnalyticsEventParams } from '../../utils';
import { useCallback } from 'react';
import { useSafeArea } from '@banhala/ui-kit';
import { useOpenDeeplink } from '../useOpenDeeplink';

const DEFAULT_FOLDER_NAME = '기본 서랍';
const DEFAULT_FOLDER_SNO = 0;
const STORAGE_KEY = 'isShownCreateLikeFolder';

const ANONYMOUS_USER_LIKE_COUNT = 5;

type UseAddLikeGoodsProps = {
  listParams?: string;
  onChangeIsLiked: (sno: number, nextValue: boolean) => void;
  screen: string;
  hideToast?: boolean;
};

export const useAddLikeGoods = ({
  onChangeIsLiked,
  listParams,
  screen,
  hideToast,
}: UseAddLikeGoodsProps) => {
  const { saveGoodsToDefaultLikeFolder } = useSaveGoodsToDefaultLikeFolder(
    onChangeIsLiked,
    screen,
    listParams,
    hideToast,
  );
  const { show: showAnonymousLikeCountLimitAlert } =
    useShowAnonymousLikeCountLimitAlert();

  const { data: originMemberInfo, refetch } = useMyInfo();

  const { show } = useCreateLikeFolderBottomSheet();
  const { show: createNewLikeFolderBottomSheet } =
    useCreateNewLikeFolderBottomSheet();

  const getHandleCondition = useCallback(async () => {
    const getMemberInfo = async () => {
      if (!originMemberInfo) {
        const reFetchedMemberInfo = await refetch().then(result => result.data);

        return reFetchedMemberInfo ?? null;
      }

      if (originMemberInfo.is_anonymous) {
        const reFetchedMemberInfo = await refetch().then(result => result.data);

        return reFetchedMemberInfo ?? originMemberInfo;
      }
      return originMemberInfo;
    };

    const memberInfo = await getMemberInfo();
    if (!memberInfo) return;

    const { like_folders_count, is_anonymous } = memberInfo;
    const isChecked = await (localStorageBridge.getBoolean(STORAGE_KEY) ??
      false);

    if (is_anonymous) {
      if (memberInfo.likes_count >= ANONYMOUS_USER_LIKE_COUNT) {
        return 'blockAnonymouseLike';
      }
      if (memberInfo.likes_count >= ANONYMOUS_USER_LIKE_COUNT - 1) {
        // 비회원이 찜 5개 찼을 때
        return 'anonymousLikeLimit';
      }
      return 'justSaveDefaultLikeFolder';
    }

    if (isChecked === false && like_folders_count < 2) {
      return 'showTutorialAlert';
    }
    if (like_folders_count > 1) {
      return 'showCreateNewLikeFolder';
    }
    if (like_folders_count === 1) {
      return 'justSaveDefaultLikeFolder';
    }
  }, [originMemberInfo, refetch]);

  const selectFolder = useCallback<
    (sno: number) => Promise<{ folderName: string; folderSno: number } | null>
  >(
    async (sno: number) => {
      const result = await show({
        goodsSno: sno,
        onChangeIsLiked,
        hideToast,
        screen,
      });
      if (result.type === 'cancel') {
        return null;
      }
      if (result.data.showCreateNewLikeFolder) {
        const result = await createNewLikeFolderBottomSheet({
          goodsSno: sno,
          listParams: listParams,
          screen,
        });
        if (result.type === 'confirm') {
          onChangeIsLiked(result.data.goodsSno, true);
          return {
            folderName: result.data.folderName,
            folderSno: result.data.folderSno,
          };
        }
      } else {
        return {
          folderName: result.data.folder!.name,
          folderSno: result.data.folder!.sno,
        };
      }
      // unreachable
      return null;
    },
    [
      createNewLikeFolderBottomSheet,
      listParams,
      onChangeIsLiked,
      screen,
      show,
      hideToast,
    ],
  );

  const showTutorialAlert = useCallback(
    (sno: number) => {
      return new Promise<{ folderName: string; folderSno: number } | null>(
        resolve => {
          systemAlert(
            '서랍 만들기',
            '서랍은 찜을 담아두는 새로운 공간이에요. 찜한 상품을 새로운 서랍에 넣어볼까요?',
            [
              {
                text: '다음에요',
                onPress: () => {
                  localStorageBridge.putBoolean(STORAGE_KEY, true);
                  saveGoodsToDefaultLikeFolder(sno);
                  resolve({
                    folderName: DEFAULT_FOLDER_NAME,
                    folderSno: DEFAULT_FOLDER_SNO,
                  });
                },
              },
              {
                text: '네',
                onPress: async () => {
                  localStorageBridge.putBoolean(STORAGE_KEY, true);
                  const result = await selectFolder(sno);
                  if (result) {
                    resolve(result);
                  }
                },
              },
            ],
          );
        },
      );
    },
    [saveGoodsToDefaultLikeFolder, selectFolder],
  );

  /**
   * - 조건에 따라 알럿 또는 바텀시트가 나온다.
   */
  const addLikeGoods = useCallback<
    (
      sno: number,
      analyticsParams?: LogAnalyticsEventParams['analytics'],
      ablyOnlyLog?: LogAnalyticsEventParams['ably_only_log'],
    ) => Promise<{
      folderName: string;
      folderSno: number;
    } | null>
  >(
    async (sno, analyticsParams, ablyOnlyLog) => {
      const handleCondition = await getHandleCondition();

      if (isNil(handleCondition)) {
        return null;
      }

      switch (handleCondition) {
        case 'blockAnonymouseLike':
        case 'anonymousLikeLimit': {
          if (handleCondition === 'anonymousLikeLimit') {
            await saveGoodsToDefaultLikeFolder(sno);
          }
          bridge.logAnalyticsEvent({
            name: 'VIEW_POPUP_LIKE_GOODS_LIMIT',
            analytics: analyticsParams,
            ably_only_log: ablyOnlyLog,
          });

          showAnonymousLikeCountLimitAlert().then(res => {
            if (res.type === 'confirm') {
              bridge.logAnalyticsEvent({
                name: 'CLICK_LIKE_POPUP_SIGN_UP',
                analytics: analyticsParams,
                ably_only_log: ablyOnlyLog,
              });
              bridge.assertLogin({});
            }
          });
          return {
            folderName: DEFAULT_FOLDER_NAME,
            folderSno: DEFAULT_FOLDER_SNO,
          };
        }
        case 'showTutorialAlert':
          return await showTutorialAlert(sno);
        case 'showCreateNewLikeFolder':
          return await selectFolder(sno);
        case 'justSaveDefaultLikeFolder': {
          saveGoodsToDefaultLikeFolder(sno);
          return {
            folderName: DEFAULT_FOLDER_NAME,
            folderSno: DEFAULT_FOLDER_SNO,
          };
        }
        default:
          return null;
      }
    },
    [
      getHandleCondition,
      showAnonymousLikeCountLimitAlert,
      showTutorialAlert,
      selectFolder,
      saveGoodsToDefaultLikeFolder,
    ],
  );

  return {
    addLikeGoods,
  };
};
const BOTTOM_DOCKED_BUTTON_HEIGHT = 56 + 12 * 2;

const useSaveGoodsToDefaultLikeFolder = (
  onChangeIsLiked: (sno: number, nextValue: boolean) => void,
  screen: string,
  listParams?: string,
  hideToast?: boolean,
) => {
  const client = useQueryClient();
  const { bottom } = useSafeArea();
  const { openDeeplink } = useOpenDeeplink();

  const { mutate: mutateLikeGoods } = useMutation({
    mutationFn: likeGoodsService.post,
    onSuccess: async ({ data }, { folder_sno, sno }) => {
      onChangeIsLiked(sno, true);

      if (!hideToast) {
        toast({
          folderName: '기본 서랍',
          folderSno: folder_sno,
          screen,
          bottomOffset: bottom + BOTTOM_DOCKED_BUTTON_HEIGHT + 12,
          loggingAnalytics: { GOODS_SNO: sno },
          openDeeplink,
        });
      }

      // queryKey.getMemberInfoQuery 갱신하기

      client.setQueryData<GetMemberInfoResponse>(
        queryKey.getMemberInfoQuery,
        oldData => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            likes_count: data.likes_count,
          };
        },
      );

      bridge.likeGoods({
        goods_sno: sno,
        is_liked: true,
        folder_sno,
      });
    },
  });

  const saveGoodsToDefaultLikeFolder = async (sno: number) => {
    mutateLikeGoods({
      list_params_referrer: listParams,
      sno,
      folder_sno: 0,
    });
  };
  return { saveGoodsToDefaultLikeFolder };
};
